import { RealtimeDataPage } from "modules/reports/RealtimeDataPage";
import { SendCommandPage } from "modules/sites/SendCommandPage";
import { UnitDashboard } from "modules/sites/UnitDashboard";
import { EventListPage } from "modules/sites/EventListPage";
import { DeviceDashboard } from "modules/sites/DeviceDashboard";
import { toLocalTimeFromUTCWithOffset } from "datetime";

import { SummaryDashboard } from "./modules/sites/SummaryDashboard";
import { SiteDashboard } from "./modules/sites/SiteDashboard";
import { DataQueryPage } from "./modules/reports/DataQueryPage";
import { AdminDevicesPage } from "./modules/admin/AdminDevicesPage";
import { AdminUsersPage } from "./modules/admin/AdminUsersPage";
import { AdminPage } from "./modules/admin/Index";
import { DeviceMessagesPage } from "./modules/devices/pages/DeviceMessagesPage";
import { MessageDetailsPage } from "./modules/devices/pages/MessageDetailsPage";
import { ReportsPage } from "./modules/reports/ReportsPage";
import { LogsPage } from "./modules/devices/pages/LogsPage";
import { ConfigPage } from "./modules/devices/pages/ConfigView";
import { EventDetailsPage } from "./modules/sites/EventDetailsPage";
import { CreateEditUser } from "./modules/admin/CreateEditUser";
import { CreateEditDevice } from "./modules/admin/CreateEditDevice";
import { WaveformsPage } from "./modules/devices/pages/WaveformsPage";
import { Permissions } from "./modules/auth/";

export interface DefinedRoute {
  path: string;
  title: string | ((args: RouteParams) => string);
  requiredPermission: Permissions;
}

interface DefinedRouteWithComponent extends DefinedRoute {
  Component: React.ComponentType<unknown>;
}

const routes = [
  {
    title: "Sites",
    path: "/",
    requiredPermission: Permissions.None,
    Component: SummaryDashboard,
  },
  /* sites */
  {
    title: ":siteId",
    path: "/sites/:siteId",
    Component: SiteDashboard,
    requiredPermission: Permissions.Site_ViewSummary,
  },
  {
    title: "Reports",
    path: "/sites/:siteId/reports",
    requiredPermission: Permissions.Site_ViewReports,
    Component: ReportsPage,
  },
  {
    title: "Real-time data",
    path: "/sites/:siteId/realtime",
    requiredPermission: Permissions.Site_ViewRealtimeData,
    Component: RealtimeDataPage,
  },
  {
    title: "Send command",
    path: "/sites/:siteId/command",
    requiredPermission: Permissions.Site_SendCommand,
    Component: SendCommandPage,
  },
  {
    title: "Events",
    path: "/sites/:siteId/events",
    requiredPermission: Permissions.Site_ViewEvents,
    Component: EventListPage,
  },
  /* units */
  {
    title: ":unitId",
    path: "/sites/:siteId/units/:unitId",
    requiredPermission: Permissions.Unit_ViewSummary,
    Component: UnitDashboard,
  },
  {
    title: "Reports",
    path: "/sites/:siteId/units/:unitId/reports",
    requiredPermission: Permissions.Unit_ViewReports,
    Component: ReportsPage,
  },
  {
    path: "/sites/:siteId/units/:unitId/realtime",
    title: "Real-time data",
    requiredPermission: Permissions.Unit_ViewRealtimeData,
    Component: RealtimeDataPage,
  },
  {
    path: "/sites/:siteId/units/:unitId/command",
    title: "Send command",
    requiredPermission: Permissions.Unit_SendCommand,
    Component: SendCommandPage,
  },
  {
    title: "Events",
    path: "/sites/:siteId/units/:unitId/events",
    requiredPermission: Permissions.Unit_ViewEvents,
    Component: EventListPage,
  },
  /* devices */
  {
    title: ":deviceId",
    path: "/sites/:siteId/units/:unitId/device/:deviceId",
    Component: DeviceDashboard,
    requiredPermission: Permissions.Devices_ViewSummary,
  },
  {
    title: "Events",
    path: "/sites/:siteId/units/:unitId/device/:deviceId/events",
    requiredPermission: Permissions.Devices_ViewEvents,
    Component: EventListPage,
  },

  {
    title: "Messages",
    path: "/sites/:siteId/units/:unitId/device/:deviceId/messages",
    requiredPermission: Permissions.Devices_ViewMessages,
    Component: DeviceMessagesPage,
  },
  {
    path: "/sites/:siteId/units/:unitId/device/:deviceId/logs",
    title: "Logs",
    requiredPermission: Permissions.Devices_ViewLogs,
    Component: LogsPage,
  },
  {
    path: "/sites/:siteId/units/:unitId/device/:deviceId/config",
    title: "Config",
    requiredPermission: Permissions.Devices_ManageConfig,
    Component: ConfigPage,
  },
  {
    path: "/sites/:siteId/units/:unitId/device/:deviceId/waveforms",
    title: "Waveforms",
    requiredPermission: Permissions.Devices_ViewWaveforms,
    Component: WaveformsPage,
  },
  {
    title: "Reports",
    path: "/sites/:siteId/units/:unitId/device/:deviceId/reports",
    requiredPermission: Permissions.Devices_ViewReports,
    Component: ReportsPage,
  },
  {
    path:
      "/sites/:siteId/units/:unitId/device/:deviceId/events/:eventTimestamp",
    title: (routeParams) =>
      `Event at ${toLocalTimeFromUTCWithOffset(routeParams["eventTimestamp"])}`,
    requiredPermission: Permissions.Devices_ViewEvents,
    Component: EventDetailsPage,
  },
  {
    path: "/sites/:siteId/units/:unitId/device/:deviceId/command",
    title: "Send command",
    requiredPermission: Permissions.Devices_SendCommand,
    Component: SendCommandPage,
  },
  {
    title: ":messageCategory / :messageType",
    path:
      "/sites/:siteId/units/:unitId/device/:deviceId/:messageCategory(data|command|response|request)/:messageType",
    requiredPermission: Permissions.Devices_ViewMessages,
    Component: MessageDetailsPage,
  },
  {
    title: "Data Query",
    path: "/dataquery",
    requiredPermission: Permissions.Devices_RunArbitraryReportQuery,
    Component: DataQueryPage,
  },
  {
    title: "Administration",
    path: "/admin",
    requiredPermission: Permissions.Users_Manage,
    Component: AdminPage,
  },
  {
    title: "Manage Users",
    path: "/admin/users",
    requiredPermission: Permissions.Users_Manage,
    Component: AdminUsersPage,
  },
  {
    title: "Create user",
    path: "/admin/users/create",
    requiredPermission: Permissions.Users_Manage,
    Component: CreateEditUser,
  },
  {
    title: "Edit user",
    path: "/admin/users/edit/:userId",
    requiredPermission: Permissions.Users_Manage,
    Component: CreateEditUser,
  },
  {
    title: "Manage Devices",
    path: "/admin/devices",
    requiredPermission: Permissions.Devices_ManageAcrossAllSites,
    Component: AdminDevicesPage,
  },
  {
    title: "Create device",
    path: "/admin/devices/create",
    requiredPermission: Permissions.Devices_ManageAcrossAllSites,
    Component: CreateEditDevice,
  },
  {
    title: "Edit device",
    path: "/admin/devices/edit/:deviceId",
    requiredPermission: Permissions.Devices_ManageAcrossAllSites,
    Component: CreateEditDevice,
  },
] as DefinedRouteWithComponent[];

export const mainNavigationRoutes = routes.filter(
  (x) => x.path === "/" || x.path.substring(1).indexOf("/") === -1
);
export default routes;

export interface RouteParams {
  [param: string]: string;
}
const substituteParameters = (params: RouteParams, text: string) =>
  Object.keys(params || {}).reduce(
    (path, param) => path.replace(`:${param}`, params[param]),
    text
  );
export const substituteRouteParameters = (
  routes: DefinedRoute[],
  params: RouteParams
) =>
  routes.map(({ path, title, ...args }) => ({
    path: substituteParameters(params, path),
    title:
      title &&
      (typeof title === "string"
        ? substituteParameters(params, title)
        : title(params)),
    ...args,
  }));
